import { addMinutes, startOfMinute } from "date-fns";
import isJSON from "validator/es/lib/isJSON";

import { QueryClient } from "@formant/core/client/QueryClient";
import { compress } from "@formant/core/common/compression/compress";
import { createPointFlattener } from "@formant/core/common/createPointFlattener";
import { defined } from "@formant/core/common/defined";
import { OutsideDataRetentionPeriodError } from "@formant/core/common/OutsideDataRetentionPeriodError";
import { throttle } from "@formant/core/common/throttle";
import { toJsonLines } from "@formant/core/common/toJsonLines";
import { TooMuchDataError } from "@formant/core/common/TooMuchDataError";
import { AggregateLevel } from "@formant/core/model/AggregateLevel";
import { IQueryStore } from "@formant/core/model/data/IQueryStore";
import { duration } from "@formant/core/model/duration";
import { IExportedDatapoint } from "@formant/core/model/IExportedDatapoint";
import { IFilter } from "@formant/core/model/IFilter";
import { IQuery } from "@formant/core/model/IQuery";
import { IStreamAggregateData } from "@formant/core/model/IStreamAggregateData";
import { IStreamData } from "@formant/core/model/IStreamData";
import { StreamType } from "@formant/core/model/StreamType";

import { track } from "../analytics";
import { filterAggregateByType } from "../component/Observe/utils/filterAggregateByType";
import { filterDataByTime } from "../component/Observe/utils/filterDataByTime";
import { filterDataByType } from "../component/Observe/utils/filterDataByType";
import { saveData } from "../component/Observe/utils/saveData";
import { IAppStore } from "./IAppStore";
import { IAuthStore } from "./IAuthStore";
import { IDeviceDetailsStore } from "./IDeviceDetailsStore";
import { IObserveStore } from "./IObserveStore";
import { IOnDemandStore } from "./IOnDemandStore";
import { QueryCache } from "./QueryCache";
import { StoreCache } from "./StoreCache";

const flattenPoints = createPointFlattener({ isJSON });

interface IModuleQueryKey {
    filter: IFilter;
    name: string | string[];
    type: StreamType;
    start: Date;
    end: Date;
}

export class QueryStore implements IQueryStore {
    private queryStoreCache = new StoreCache<
        IQuery,
        IStreamData[] | "too much data"
    >({
        capacity: 10000,
        staleIntervalMs: 200 * duration.millisecond,
    });
    private queryAggregateStoreCache = new StoreCache<
        IQuery,
        IStreamAggregateData[] | "too much data"
    >({
        capacity: 10000,
        staleIntervalMs: 200 * duration.millisecond,
    });
    private moduleQueryCache = new StoreCache<
        IModuleQueryKey,
        IStreamData[] | "too much data"
    >({
        capacity: 10000,
        staleIntervalMs: 200 * duration.millisecond,
    });
    private queryCache!: QueryCache;
    private trackOutsideDataRetentionPeriod = throttle(
        () => track("Query outside data retention period"),
        5 * duration.second,
    );
    private showTooMuchDataToast = throttle(
        () => this.appStore.setSnackbar("Too much data in selected time range"),
        5 * duration.second,
    );

    constructor(
        private queryClient: QueryClient,
        private authStore: IAuthStore,
        private appStore: IAppStore,
        private deviceDetailsStore: IDeviceDetailsStore,
        private observeStore: IObserveStore,
        onDemandStore: IOnDemandStore,
    ) {
        this.queryCache = new QueryCache(queryClient, authStore, onDemandStore);
    }

    query(query: IQuery): IStreamData[] | "too much data" | undefined {
        return this.queryStoreCache.get(query, async () => {
            try {
                return await this.queryCache.query(
                    await this.authStore.token(),
                    query,
                );
            } catch (error) {
                if (error instanceof TooMuchDataError) {
                    this.showTooMuchDataToast();
                    return "too much data";
                } else if (error instanceof OutsideDataRetentionPeriodError) {
                    this.trackOutsideDataRetentionPeriod();
                    this.setSnackbarOutsideDataRetentionPeriod();
                    return [];
                }
                throw error;
            }
        });
    }

    async queryAsync(query: IQuery): Promise<IStreamData[]> {
        const data = await this.queryStoreCache.getAsync(query, async () => {
            try {
                return await this.queryCache.query(
                    await this.authStore.token(),
                    query,
                );
            } catch (error) {
                if (error instanceof TooMuchDataError) {
                    this.showTooMuchDataToast();
                    return "too much data";
                } else if (error instanceof OutsideDataRetentionPeriodError) {
                    this.trackOutsideDataRetentionPeriod();
                    this.setSnackbarOutsideDataRetentionPeriod();
                    return [];
                }
                throw error;
            }
        });

        if (data === "too much data") {
            throw new TooMuchDataError();
        }
        return data;
    }

    async fetchQuery(query: IQuery) {
        return await this.queryClient.query(
            await this.authStore.token(),
            query,
        );
    }

    queryAggregates(
        query: IQuery,
    ): IStreamAggregateData[] | "too much data" | undefined {
        return this.queryAggregateStoreCache.get(query, async () => {
            try {
                return await this.queryCache.queryAggregate(
                    await this.authStore.token(),
                    query,
                );
            } catch (error) {
                if (error instanceof TooMuchDataError) {
                    this.showTooMuchDataToast();
                    return "too much data";
                } else if (error instanceof OutsideDataRetentionPeriodError) {
                    this.trackOutsideDataRetentionPeriod();
                    this.setSnackbarOutsideDataRetentionPeriod();
                    return [];
                }
                throw error;
            }
        });
    }

    async queryAggregatesAsync(query: IQuery): Promise<IStreamAggregateData[]> {
        const data = await this.queryAggregateStoreCache.getAsync(
            query,
            async () => {
                try {
                    return await this.queryCache.queryAggregateByHour(
                        await this.authStore.token(),
                        query,
                    );
                } catch (error) {
                    if (error instanceof TooMuchDataError) {
                        this.showTooMuchDataToast();
                        return "too much data";
                    } else if (
                        error instanceof OutsideDataRetentionPeriodError
                    ) {
                        this.trackOutsideDataRetentionPeriod();
                        this.setSnackbarOutsideDataRetentionPeriod();
                        return [];
                    }
                    throw error;
                }
            },
        );

        if (data === "too much data") {
            throw new TooMuchDataError();
        }
        return data;
    }

    moduleQuery<T extends StreamType>(
        filter: IFilter,
        name: string | string[],
        type: T,
        start: Date,
        end: Date,
    ): IStreamData<T>[] | "too much data" | undefined {
        const { clampToRetentionPeriod } = this.observeStore;
        const q: IQuery = {
            ...filter,
            names: typeof name === "string" ? [name] : name,
            types: [type],
            start: clampToRetentionPeriod(startOfMinute(start)).toISOString(),
            end: addMinutes(startOfMinute(end), 1).toISOString(),
        };
        const data = this.query(q);

        if (!data || data === "too much data") {
            return data;
        }

        const cached = this.moduleQueryCache.get(
            { filter, name, type, start, end },
            async () => filterDataByTime(data, start, end),
        );

        if (!cached || cached === "too much data") {
            return cached;
        }

        return filterDataByType(cached, type);
    }

    async fetchModuleQuery<T extends StreamType>(
        filter: IFilter,
        name: string | string[],
        type: T,
        start: Date,
        end: Date,
    ): Promise<IStreamData<T>[] | "too much data" | undefined> {
        const { clampToRetentionPeriod } = this.observeStore;
        const q: IQuery = {
            ...filter,
            names: typeof name === "string" ? [name] : name,
            types: [type],
            start: clampToRetentionPeriod(startOfMinute(start)).toISOString(),
            end: addMinutes(startOfMinute(end), 1).toISOString(),
        };
        const data = this.query(q);

        if (!data || data === "too much data") {
            return data;
        }

        const cached = await this.moduleQueryCache.getAsync(
            { filter, name, type, start, end },
            async () => filterDataByTime(data, start, end),
        );

        if (!cached || cached === "too much data") {
            return cached;
        }

        return filterDataByType(cached, type);
    }

    moduleQueryAggregates<T extends StreamType>(
        filter: IFilter,
        name: string,
        type: T,
        start: Date,
        end: Date,
        aggregate: AggregateLevel,
    ): IStreamAggregateData<T>[] | "too much data" | undefined {
        const { clampToRetentionPeriod } = this.observeStore;
        const data = this.queryAggregates({
            ...filter,
            names: [name],
            types: [type],
            start: clampToRetentionPeriod(startOfMinute(start)).toISOString(),
            end: addMinutes(startOfMinute(end), 1).toISOString(),
            aggregate,
        });

        if (!data || data === "too much data") {
            return data;
        }

        return filterAggregateByType(data, type);
    }

    async export(query: IQuery): Promise<void> {
        try {
            const deviceNames = new Map(
                (
                    (await this.deviceDetailsStore.listAsync({
                        type: "default",
                    })) || []
                ).map(_ => [_.id, _.name]),
            );
            const result = await this.queryCache.query(
                await this.authStore.token(),
                query,
            );
            const flat = result.reduce(
                (acc, _) => [
                    ...acc,
                    ...flattenPoints(_).map(point => ({
                        deviceId: _.deviceId,
                        deviceName: defined(deviceNames.get(_.deviceId)),
                        streamName: point.streamName,
                        streamType: point.streamType,
                        tags: _.tags,
                        timestamp: point.timestamp,
                        value: point.value,
                    })),
                ],
                [] as IExportedDatapoint[],
            );
            const lines = toJsonLines(flat);
            const compressed = await compress(lines);
            track("Export Telemetry (Direct Download)");
            saveData(
                `FormantExport-${Date.now()}.jsonl.gz`,
                compressed as BlobPart,
                "application/gzip",
            );
        } catch (error) {
            if (
                error instanceof TooMuchDataError ||
                (error instanceof Error &&
                    error.message &&
                    (error.message.includes("Invalid array length") ||
                        error.message.includes("Invalid string length")))
            ) {
                this.appStore.setSnackbar(
                    "Too much data in selected time range",
                );
            } else if (error instanceof OutsideDataRetentionPeriodError) {
                this.appStore.setSnackbar(
                    "Selected time range is outside data retention period",
                );
            } else {
                throw error;
            }
        }
    }

    async exportSheet(query: IQuery): Promise<void> {
        try {
            const { spreadsheetUrl, telemetryNotFound } =
                await this.queryClient.exportSheet(
                    await this.authStore.token(),
                    query,
                );
            if (telemetryNotFound) {
                this.appStore.setSnackbar("Telemetry not found");
            } else {
                window.open(spreadsheetUrl, "_blank");
            }
            track("Export Telemetry (Google Sheets)");
        } catch (error) {
            if (error instanceof TooMuchDataError) {
                this.appStore.setSnackbar(
                    "Too much data in selected time range",
                );
            } else if (error instanceof OutsideDataRetentionPeriodError) {
                this.appStore.setSnackbar(
                    "Selected time range is outside data retention period",
                );
            } else {
                throw error;
            }
        }
    }

    private setSnackbarOutsideDataRetentionPeriod = () =>
        this.appStore.setSnackbar(
            "Data unavailable, query outside data retention period.",
        );
}
